import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { reportRenderError } from '@atom/utilities/fullstoryUtilities';

import CreateControls from './CreateControls';
import Portal from './Portal';
import Profile from './Profile';

import classes from './navigation.module.css';

const AiChat = lazy(() => import('@atom/components/aiChat/AiChat'));
const TeamChat = lazy(() => import('@atom/components/teamChat/TeamChat'));

/*
 * The UnknownError route renders this component also. A child that throws
 * after a network or type error crashes the error page, which remounts the
 * component and requests again, recursively. Wrap any child that fetches in
 * its own ErrorBoundary (see AiChat).
 */
const NavControls = () => {
  const preferences = usePreferences();
  const [, { pathname }] = useNavigation();
  const isAiEnabled = !!preferences?.ai?.enabled;
  const aiRoleSet = preferences?.ai?.roleSet;
  const hasPermission = aiRoleSet && hasRolePermissions(ROLE_SETS[aiRoleSet]);
  const showAi = isAiEnabled && hasPermission;
  const showTeamChat = !!preferences?.teamChat?.enabled;

  return (
    <div className={classes.navControlsContainer}>
      <div className={classes.additionalButtonsWrapper}>
        <div className={classes.additionalButtonsContainer}>
          {showAi && (
            <ErrorBoundary
              fallback={null}
              onError={error => reportRenderError(error, 'AiChat')}
            >
              <Suspense fallback={null}>
                <AiChat />
              </Suspense>
            </ErrorBoundary>
          )}
          {showTeamChat && (
            <ErrorBoundary
              fallback={null}
              onError={error => reportRenderError(error, 'TeamChat')}
            >
              <Suspense fallback={null}>
                <TeamChat />
              </Suspense>
            </ErrorBoundary>
          )}
        </div>
      </div>
      {hasRolePermissions(ROLE_SETS.INSPECTOR) && <CreateControls />}
      <Portal pathname={pathname} />
      <Profile />
    </div>
  );
};

export default NavControls;
